import React, { useEffect, useState } from 'react'

import getModels, { GetModelsOptions, Model } from 'api/vehicles/getModels'
import { FormikDropdown } from 'components/FormikFields'
import { FormikDropdownChild } from 'components/FormikFields/FormikDropdown/FormikDropdown'
import { setFieldValueAndTouched } from 'form/fields/utils/fieldUtils'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'

import { LinkStyle } from '../VehicleSelectionBase.styled'

interface modelProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any
    lenderId?: number
    disable?: boolean
    dataSource?: string[]
    assetType?: unknown
    primaryVehicleId?: number
    vehicleNumber: number
    prefilled?: boolean
    vehicleTypeList?: string[]
    excludeVehicleTypesList?: string[]
    excludeModelNames?: string[]
    onWriteInModelOpen?: () => void
    captive?: string
}

const defaultProps = {
    lenderId: null,
    disable: null,
    dataSource: null,
    assetType: null,
    primaryVehicleId: null,
    prefilled: false,
    vehicleTypeList: null,
    excludeVehicleTypesList: null,
    excludeModelNames: null,
    captive: undefined,
}

function getVehicleFieldPath(isFirstVehicle: boolean, field: string): string {
    const prefix = isFirstVehicle ? 'vehicle.primary' : 'vehicle.secondary'

    return `${prefix}.${field}`
}

function handleModelsResponse(
    models: Model[],
    currentModel: string,
    isFirstVehicle: boolean,
    isPrefilled: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any,
    vehicleFieldName: string,
) {
    const selectedModel = models.filter((e) => `${e.id}` === currentModel)

    if (!isPrefilled && selectedModel.length === 0) {
        formik.setFieldValue(getVehicleFieldPath(isFirstVehicle, 'modelId'), '')

        formik.setFieldValue(
            getVehicleFieldPath(isFirstVehicle, 'selectedVehicleId'),
            '',
        )

        if (isFirstVehicle) {
            formik.setFieldValue('vehicle.vehicleType', '')
        }
    } else if (selectedModel.length > 0) {
        formik.setFieldValue(
            getVehicleFieldPath(isFirstVehicle, 'modelId'),
            currentModel,
        )
    }

    if (models.length === 1) {
        setFieldValueAndTouched(
            formik,
            vehicleFieldName,
            models[0].id.toString(),
        )
    }
}

export default function ModelDropdown({
    formik,
    lenderId,
    disable,
    dataSource,
    assetType,
    primaryVehicleId,
    vehicleNumber,
    prefilled,
    vehicleTypeList,
    excludeVehicleTypesList,
    excludeModelNames,
    onWriteInModelOpen,
    captive,
}: modelProps) {
    const [response, updateResponse] = useState([] as Model[])
    const { vehicle } = formik.values
    const { condition } = vehicle
    const FIRST_VEHICLE = 1
    const IS_FIRST_VEHICLE = vehicleNumber === FIRST_VEHICLE
    const vehicleFieldName = getVehicleFieldPath(IS_FIRST_VEHICLE, 'modelId')

    const year = IS_FIRST_VEHICLE
        ? vehicle.primary.year
        : vehicle.secondary.year

    const { makeId } = vehicle

    const model = IS_FIRST_VEHICLE
        ? vehicle.primary.modelId
        : vehicle.secondary.modelId

    const isModelSelectionIncomplete = !makeId || !year

    useEffect(() => {
        const abortController = new AbortController()

        if (condition && year && makeId) {
            const getModelsOptions = {
                condition,
                year,
                makeId,
                lenderId,
                disable,
                dataSource,
                assetType,
                primaryVehicleId,
                vehicleTypeList,
                excludeVehicleTypesList,
                captive,
            } as GetModelsOptions

            if (!IS_FIRST_VEHICLE) {
                getModelsOptions.primaryVehicleId =
                    formik.values.vehicle.primary.selectedVehicleId.split(
                        ':',
                    )[0]
            }

            updateResponse([])

            getModels(getModelsOptions, abortController)
                .then((models: Model[]) => {
                    const filtered = excludeModelNames?.length
                        ? models.filter(
                              (m) => !excludeModelNames.includes(m.name),
                          )
                        : models

                    updateResponse(filtered)

                    handleModelsResponse(
                        filtered,
                        model,
                        IS_FIRST_VEHICLE,
                        !!prefilled,
                        formik,
                        vehicleFieldName,
                    )
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        OctaneLogger.error(
                            `Unexpected error when trying to load models.`,
                            undefined,
                            error as Error,
                        )
                    }
                })
        }

        return () => abortController.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        condition,
        year,
        makeId,
        vehicleTypeList,
        excludeVehicleTypesList,
        excludeModelNames,
        assetType,
    ])

    if (isModelSelectionIncomplete && !prefilled) {
        return (
            <FormikDropdown
                name={vehicleFieldName}
                label="Model"
                placeholder={prefilled ? '' : 'Select Model'}
                disabled
            >
                {{ name: '', value: '' }}
                {{ name: '', value: '' }}
            </FormikDropdown>
        )
    }

    const modelName = IS_FIRST_VEHICLE
        ? vehicle.primary.model
        : vehicle.secondary?.model

    if (prefilled && modelName) {
        return (
            <FormikDropdown
                name={vehicleFieldName}
                label="Model"
                placeholder=""
                prefilled={prefilled}
            >
                {{ name: modelName, value: model }}
                {{ name: modelName, value: model }}
            </FormikDropdown>
        )
    }

    const noMatchComponent = onWriteInModelOpen && (
        <p>
            <LinkStyle type="button" onClick={onWriteInModelOpen}>
                Add vehicle manually
            </LinkStyle>
        </p>
    )

    return (
        <FormikDropdown
            name={vehicleFieldName}
            label="Model"
            placeholder={prefilled ? '' : 'Select Model'}
            error={model === null || model === ''}
            searchable
            searchPlaceholder="Search models..."
            prefilled={prefilled}
            noMatchComponent={noMatchComponent}
        >
            {response.map((child: Model) => {
                const newBorn: FormikDropdownChild = {
                    name: child.name,
                    value: child.id.toString(),
                }

                return newBorn
            })}
        </FormikDropdown>
    )
}

ModelDropdown.defaultProps = defaultProps
